import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { BrowserCacheLocation, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@env';

/**
 * Handles logging of messages at a specific log level.
 *
 * @param {LogLevel} logLevel - The log level indicating the severity or type of the log.
 * @param {string} message - The message to be logged.
 * @returns {void} This function does not return a value.
 */
export function loggerCallback(logLevel: LogLevel, message: string): void {
  // eslint-disable-next-line no-console
  console.debug(LogLevel[logLevel], message);
}

/**
 * Factory function to create and configure an instance of MSAL's PublicClientApplication.
 *
 * This function initializes the MSAL library with authentication and cache configurations,
 * enabling interactions with Azure Active Directory for authentication purposes.
 *
 * @returns {IPublicClientApplication} An instance of the configured Pu2blicClientApplication.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: `${environment.msal.authority}/${environment.msal.tenantId}`,
      redirectUri: environment.msal.redirectUri,
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: true,
      loggerOptions: {
        loggerCallback,
        logLevel: environment.productionBuild ? LogLevel.Error : LogLevel.Info,
        piiLoggingEnabled: !environment.productionBuild,
      },
    },
  });
}

/**
 * Factory function to create and configure the MSAL interceptor.
 *
 * @returns {MsalInterceptorConfiguration} The configuration object for the MSAL interceptor containing the interaction type and protected resource map.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>([
    [environment.msal.apiConfig.uri, environment.msal.apiConfig.scopes],
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
/**
 * Factory function to provide configuration for MSAL Guard.
 *
 * @returns {MsalGuardConfiguration} Returns the configuration for MSAL Guard, including interaction type, authorization request parameters, and login failure route.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.msal.apiConfig.scopes],
    },

    loginFailedRoute: '/dashboard',
  };
}
